/**
 * Sound.
 *
 * Every cue is SYNTHESISED with the Web Audio API — there are no audio files.
 * Three reasons, in order of importance:
 *
 *   1. The privacy notice promises the page loads nothing from anywhere else,
 *      and the CSP enforces it. Synthesis keeps that true without hunting for
 *      licensed samples we would then have to host and attribute.
 *   2. A set of arcade samples is hundreds of kilobytes for a game whose whole
 *      bundle is around a hundred.
 *   3. A cue defined in code can be tuned in one line, and every screen's
 *      palette can share one oscillator vocabulary rather than a folder of
 *      near-duplicate files.
 *
 * Browsers refuse to start an AudioContext before a user gesture, so nothing
 * here can make a noise on page load even if it wanted to. The context is
 * created lazily on the first cue, which by definition follows a click.
 */

const STORAGE_KEY = 'devtool-detective.sound';

/** Quiet by default. This plays under someone's actual work. */
const DEFAULT_VOLUME = 0.35;

export interface SoundPrefs {
  muted: boolean;
  /** 0..1, before the master trim below. */
  volume: number;
}

/**
 * Master trim.
 *
 * Applied on top of the user's volume so that "1.0" in the UI is a comfortable
 * maximum rather than a painful one. Tuned against the loudest cue (fanfare).
 */
const MASTER_TRIM = 0.22;

let prefs: SoundPrefs = load();
let context: AudioContext | null = null;
let master: GainNode | null = null;
const listeners = new Set<(p: SoundPrefs) => void>();

function load(): SoundPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { muted: false, volume: DEFAULT_VOLUME };
    const parsed = JSON.parse(raw) as Partial<SoundPrefs>;
    return {
      muted: Boolean(parsed.muted),
      volume:
        typeof parsed.volume === 'number' && parsed.volume >= 0 && parsed.volume <= 1
          ? parsed.volume
          : DEFAULT_VOLUME,
    };
  } catch {
    return { muted: false, volume: DEFAULT_VOLUME };
  }
}

function persist() {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* best effort; a lost preference is not worth an error */
  }
}

export function getPrefs(): SoundPrefs {
  return prefs;
}

export function subscribe(listener: (p: SoundPrefs) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function announce() {
  for (const listener of listeners) listener(prefs);
}

export function setMuted(muted: boolean) {
  prefs = { ...prefs, muted };
  persist();
  applyGain();
  announce();
}

export function setVolume(volume: number) {
  prefs = { ...prefs, volume: Math.min(1, Math.max(0, volume)) };
  persist();
  applyGain();
  announce();
}

function applyGain() {
  if (!master || !context) return;
  const level = prefs.muted ? 0 : prefs.volume * MASTER_TRIM;
  master.gain.setTargetAtTime(level, context.currentTime, 0.01);
}

/** Create the context on demand. Returns null if audio is unavailable. */
function audio(): { ctx: AudioContext; out: GainNode } | null {
  if (prefs.muted) return null;
  try {
    if (!context) {
      const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      context = new Ctor();
      master = context.createGain();
      master.connect(context.destination);
      applyGain();
    }
    // Safari and Chrome suspend the context until a gesture; a cue is always
    // triggered by one, so resuming here is safe and silent otherwise.
    if (context.state === 'suspended') void context.resume();
    if (!master) return null;
    return { ctx: context, out: master };
  } catch {
    return null;
  }
}

interface ToneSpec {
  freq: number;
  /** Seconds from now. */
  at?: number;
  duration?: number;
  type?: OscillatorType;
  /** Peak gain for this tone, before the master. */
  gain?: number;
  /** Slide to this frequency across the tone. */
  glideTo?: number;
}

/** One oscillator with an envelope. The building block of every cue. */
function tone({ freq, at = 0, duration = 0.12, type = 'square', gain = 0.5, glideTo }: ToneSpec) {
  const a = audio();
  if (!a) return;
  const start = a.ctx.currentTime + at;

  const osc = a.ctx.createOscillator();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, start);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(Math.max(1, glideTo), start + duration);

  const env = a.ctx.createGain();
  // A tiny attack rather than an instant one: a square wave starting at full
  // amplitude clicks, and the click is louder than the note.
  env.gain.setValueAtTime(0.0001, start);
  env.gain.exponentialRampToValueAtTime(gain, start + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  osc.connect(env);
  env.connect(a.out);
  osc.start(start);
  osc.stop(start + duration + 0.02);
}

/** Filtered noise, for typewriter keys and stamps. */
function noise(at = 0, duration = 0.08, frequency = 1200, gain = 0.35) {
  const a = audio();
  if (!a) return;
  const start = a.ctx.currentTime + at;
  const frames = Math.floor(a.ctx.sampleRate * duration);
  const buffer = a.ctx.createBuffer(1, frames, a.ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i += 1) data[i] = Math.random() * 2 - 1;

  const source = a.ctx.createBufferSource();
  source.buffer = buffer;

  const filter = a.ctx.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = frequency;
  filter.Q.value = 0.8;

  const env = a.ctx.createGain();
  env.gain.setValueAtTime(gain, start);
  env.gain.exponentialRampToValueAtTime(0.0001, start + duration);

  source.connect(filter);
  filter.connect(env);
  env.connect(a.out);
  source.start(start);
  source.stop(start + duration);
}

/**
 * The cue vocabulary.
 *
 * Grouped by where they are used, which is how the palettes differ per screen:
 * the menus are arcade blips, the briefing is a low noir room, play is dry
 * mechanical clicks, and the verdict and the leaderboard are the only places
 * allowed a tune.
 */
export type Cue =
  // Menus and navigation: bright, short, arcade.
  | 'move'
  | 'select'
  | 'back'
  // Briefing: low, sparse, noir.
  | 'caseOpen'
  | 'type'
  // Play: dry mechanical, no pitch drama.
  | 'accuse'
  | 'retract'
  | 'submit'
  // Verdict: the only fanfare in the game.
  | 'found'
  | 'missed'
  | 'stamp'
  | 'solved'
  | 'cold'
  // Leaderboard: unmistakably a cabinet.
  | 'coin'
  | 'highScore';

export function play(cue: Cue) {
  switch (cue) {
    // ---------------------------------------------------------- navigation
    case 'move':
      tone({ freq: 660, duration: 0.05, gain: 0.25, type: 'square' });
      break;
    case 'select':
      tone({ freq: 880, duration: 0.06, gain: 0.35 });
      tone({ freq: 1320, at: 0.055, duration: 0.08, gain: 0.3 });
      break;
    case 'back':
      tone({ freq: 520, duration: 0.07, gain: 0.28, glideTo: 320 });
      break;

    // ------------------------------------------------------------ briefing
    // A door closing on a quiet room: two low tones, no sparkle.
    case 'caseOpen':
      tone({ freq: 196, duration: 0.5, gain: 0.3, type: 'triangle' });
      tone({ freq: 147, at: 0.12, duration: 0.6, gain: 0.26, type: 'triangle' });
      noise(0, 0.16, 300, 0.12);
      break;
    case 'type':
      noise(0, 0.035, 2200, 0.22);
      break;

    // ---------------------------------------------------------------- play
    case 'accuse':
      tone({ freq: 440, duration: 0.05, gain: 0.3, type: 'square' });
      noise(0, 0.04, 1800, 0.16);
      break;
    case 'retract':
      tone({ freq: 300, duration: 0.05, gain: 0.22, type: 'square' });
      break;
    case 'submit':
      tone({ freq: 392, duration: 0.09, gain: 0.32 });
      tone({ freq: 523, at: 0.09, duration: 0.12, gain: 0.32 });
      break;

    // ------------------------------------------------------------- verdict
    case 'found':
      tone({ freq: 784, duration: 0.09, gain: 0.3 });
      tone({ freq: 1047, at: 0.08, duration: 0.1, gain: 0.28 });
      break;
    case 'missed':
      tone({ freq: 330, duration: 0.14, gain: 0.26, type: 'sawtooth', glideTo: 220 });
      break;
    case 'stamp':
      // The thump of the verdict stamp landing.
      noise(0, 0.12, 220, 0.5);
      tone({ freq: 90, duration: 0.16, gain: 0.45, type: 'triangle' });
      break;
    case 'solved':
      // Rising major arpeggio: case closed.
      [523, 659, 784, 1047].forEach((freq, i) =>
        tone({ freq, at: i * 0.085, duration: 0.16, gain: 0.3 }),
      );
      break;
    case 'cold':
      // Falling minor: cold case.
      [440, 392, 311].forEach((freq, i) =>
        tone({ freq, at: i * 0.12, duration: 0.2, gain: 0.26, type: 'triangle' }),
      );
      break;

    // --------------------------------------------------------- leaderboard
    case 'coin':
      tone({ freq: 988, duration: 0.06, gain: 0.32 });
      tone({ freq: 1319, at: 0.06, duration: 0.16, gain: 0.3 });
      break;
    case 'highScore':
      [659, 784, 988, 1319, 988, 1319].forEach((freq, i) =>
        tone({ freq, at: i * 0.1, duration: 0.14, gain: 0.28 }),
      );
      break;
  }
}
